/* eslint-disable no-console */

import { useDispatch } from "react-redux"
import { hideModal, showErrorNotification, showModal, showSuccesNotification, showSuccessNotification } from "../../../../../../redux/actions"
import { useArchiveEventMutation, useBulkArchiveEventsMutation, useGetEventsByPartnerListingIdQuery, useBulkUpdateEventsMutation, useImportEventsMutation, useLazyGetEventImportByIdQuery, useLazyGetEventsByPartnerListingIdQuery } from "../../../../../../redux/slices/eventsSlice"
import { useOutletContext, useParams } from "react-router-dom"
import { useGetOffersLookupsQuery } from "../../../../../../redux/slices/lookupsSlice"
import { useEffect, useRef } from "react"

const useEventsList = () => {
    const dispatch = useDispatch()
    const { id: partnerListingId } = useParams()
    const {
        offers
    } = useOutletContext()
    const lookups = useGetOffersLookupsQuery()
    const interval = useRef(null)
    const importCounter = useRef(0)

    const {
        data: events,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,
    } = useGetEventsByPartnerListingIdQuery(partnerListingId)

    const [archiveEvent] = useArchiveEventMutation()
    const [bulkArchiveEvents] = useBulkArchiveEventsMutation()
    const [bulkUpdateEvents] = useBulkUpdateEventsMutation()
    const [importEvents] = useImportEventsMutation()
    const [getEventImport] = useLazyGetEventImportByIdQuery()
    const [getEvents] = useLazyGetEventsByPartnerListingIdQuery()

    const onClickImportEvents = () => {
        dispatch(showModal('IMPORT_EVENTS_MODAL', {
            onClickSubmit: async ({ eventsFile }) => {
                if (interval.current) {
                    clearInterval(interval.current)
                }
                importCounter.current = 0

                const res = await importEvents({ id: partnerListingId, body: eventsFile })

                if (res?.data?.success) {
                    return new Promise((resolve, reject) => {
                        interval.current = setInterval(async () => {
                            if (importCounter.current < 31) {
                                importCounter.current += 2
                                try {
                                    const importRes = await getEventImport(res.data.importId).unwrap()
                                    if (importRes?.status === 2) {
                                        clearInterval(interval.current)
                                        if (importRes.errors?.length === 0) {
                                            dispatch(showSuccesNotification('Imported events successfully.'))
                                            await getEvents(partnerListingId)
                                        }
                                        resolve({ errors: importRes.errors ?? [] })
                                    } else if (importRes?.status === 3) {
                                        clearInterval(interval.current)
                                        reject(new Error('There was an error while attempting to import the events.'))
                                    }
                                } catch (err) {
                                    clearInterval(interval.current)
                                    reject(err)
                                }
                            } else {
                                clearInterval(interval.current)
                                reject(new Error('Import timed out. Please try again.'))
                            }
                        }, 2000)
                    })
                } else if (res?.data?.errorMessage) {
                    throw new Error(res.data.errorMessage)
                } else {
                    throw new Error('There was an error while attempting to import the events.')
                }
            },
            onClickClose: () => {
                clearInterval(interval.current)
                dispatch(hideModal())
            },
        }))
    }

    const openArchiveEventModal = (props) => dispatch(showModal('ARCHIVE_EVENT_MODAL', props))
    const openGovXIdLinkModal = (offer) => dispatch(showModal('VIEW_GOVX_LINK', {
        offer,
        title: 'GOVX ID Link',
        message: `Use this link to advertise the event on your website. Customers that
          click the link will be asked to log in or verify before accessing the
          discount.` }))
    const openBulkEditEventsModal = (props) => dispatch(showModal('BULK_EDIT_EVENTS_MODAL', props))

    const onClickBulkEditEvents = async (eventIds) => {
        openBulkEditEventsModal({
            eventIds,
            offers,
            lookups,
            onClickSubmit: async (data) => {
                try {
                    await bulkUpdateEvents(data).unwrap()
                } catch (error) {
                    console.error(error)
                    dispatch(showErrorNotification(`Something went wrong, we weren't able to save the events changes.`))
                    dispatch(hideModal())

                } finally {
                    dispatch(showSuccesNotification(`${eventIds?.length} event${eventIds?.length > 1 ? 's' : ''} successfully updated.`))
                    dispatch(hideModal())
                }
            },
        })
    }

    const onClickArchiveEvents = (eventIds) => {
        openArchiveEventModal({
            eventIds,
            onClickSubmit: async (eventIds) => {
                try {
                    if (eventIds?.length === 1) {
                        await archiveEvent(eventIds[0]).unwrap()
                    } else[
                        await bulkArchiveEvents(eventIds).unwrap()
                    ]
                } catch (error) {
                    dispatch(showErrorNotification(`Something went wrong, we weren't able to archive the event${eventIds?.length > 1 ? 's' : ''}.`))
                    dispatch(hideModal())
                } finally {
                    dispatch(showSuccessNotification(`The event${eventIds?.length > 1 ? 's' : ''} ${eventIds?.length > 1 ? 'have' : 'has'} been archived.`))
                    dispatch(hideModal())
                }
            },
        })
    }

    useEffect(() => {
        if (error) {
            dispatch(showErrorNotification('Encountered an error while trying to load events for this listing.'))
        }
    }, [error, dispatch])

    useEffect(() => {
        return () => clearInterval(interval.current)
    }, [])

    return {
        events,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,
        onClickImportEvents,
        onClickArchiveEvents,
        openGovXIdLinkModal,
        onClickBulkEditEvents
    }
}

export default useEventsList
